import React from "react";
import { motion } from "framer-motion";

const floatingCards = [
  {
    cls: "animate-float1",
    style: {
      background: "linear-gradient(135deg,rgba(26,107,255,.2),rgba(10,22,40,.8))",
      border: "1px solid rgba(26,107,255,.3)",
      width: "260px",
      top: 0,
      right: 0,
    },
    content: (
      <>
        <div style={{ fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--slate)", marginBottom: "1.2rem" }}>Cloud Uptime</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "1.9rem", fontWeight: 800, color: "#fff", letterSpacing: "-.03em" }}>99.98%</div>
        <div style={{ fontSize: ".75rem", color: "var(--slate)", marginTop: "1.6rem" }}>Avg. across all deployments</div>
        <div style={{ marginTop: "3.2rem", height: "4px", borderRadius: "4px", background: "rgba(255,255,255,.08)", overflow: "hidden" }}>
          <div className="section-padding" style={{ height: "100%", borderRadius: "4px", background: "linear-gradient(90deg,var(--blue),#7eb8ff)", "--bar-width": "99%" }} />
        </div>
      </>
    ),
  },
  {
    cls: "animate-float2",
    style: {
      background: "rgba(15,31,61,.85)",
      border: "1px solid rgba(45,125,255,.15)",
      width: "220px",
      top: "140px",
      left: 0,
    },
    content: (
      <>
        <div style={{ fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--slate)", marginBottom: ".6rem" }}>Active Projects</div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {["AWS Migration – APAC", "ML Pipeline – FinTech", "React Portal – Healthcare"].map((item) => (
            <li key={item} style={{ fontSize: ".8rem", color: "var(--steel)", padding: ".25rem 0", borderBottom: "1px solid rgba(255,255,255,.05)", display: "flex", alignItems: "center", gap: ".5rem" }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--blue2)", flexShrink: 0, display: "inline-block" }} />
              {item}
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    cls: "animate-float3",
    style: {
      background: "linear-gradient(135deg,rgba(10,150,100,.15),rgba(10,22,40,.85))",
      border: "1px solid rgba(10,150,100,.25)",
      width: "240px",
      bottom: 0,
      right: "30px",
    },
    content: (
      <>
        <div style={{ fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--slate)", marginBottom: ".6rem" }}>Cost Reduction</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "1.9rem", fontWeight: 800, color: "#fff", letterSpacing: "-.03em" }}>-42%</div>
        <div style={{ fontSize: ".75rem", color: "var(--slate)", marginTop: ".2rem" }}>Avg. infrastructure savings</div>
      </>
    ),
  },
];

const stats = [
  { value: "500+", label: "Projects Delivered" },
  { value: "99%", label: "Client Satisfaction" },
  { value: "15yr", label: "Industry Experience" },
];

const Hero = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative flex items-center overflow-hidden"
      style={{ minHeight: "100vh", paddingTop: "120px", paddingBottom: "80px", paddingLeft: "5%", paddingRight: "5%" }}
      aria-label="Hero section"
    >
      {/* Backgrounds */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 80% 60% at 60% 30%,rgba(26,107,255,0.12) 0%,transparent 70%),linear-gradient(180deg,var(--navy) 0%,var(--navy2) 100%)",
          zIndex: 0,
        }}
      />
      <div className="hero-grid-lines" aria-hidden="true" />

      <div
        className="relative z-10 w-full max-w-[1200px] mx-auto grid gap-16 items-center"
        style={{ gridTemplateColumns: "1fr 1fr" }}
      >
        {/* Left */}
        <div>
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
              duration: 0.8, 
              delay: 0.3,
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
            className="inline-flex items-center gap-2 rounded-full text-xs font-semibold mb-6"
            style={{
              background: "rgba(26,107,255,0.1)",
              border: "1px solid rgba(26,107,255,0.3)",
              padding: ".4rem 1rem .4rem .6rem",
              color: "var(--blue2)",
            }}
            aria-label="Trusted by 100+ global enterprises"
          >
            <span
              className="animate-pulse-dot"
              style={{ width: "7px", height: "7px", background: "var(--blue2)", borderRadius: "50%", display: "inline-block" }}
              aria-hidden="true"
            />
            Trusted by 100+ Global Enterprises
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
              duration: 0.8, 
              delay: 0.4,
              type: "spring",
              stiffness: 80,
              damping: 20
            }}
            className="font-display text-white mb-6 text-6xl"
            style={{ lineHeight: 1.05, letterSpacing: "-0.025em" }}
          >
            Transform Your Business with<br/>
            <span className="gradient-text-blue">Enterprise-Grade Technology</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base leading-8 max-w-[480px] mb-9"
            style={{ color: "var(--slate)" }}
          >
            Accelerate your digital transformation with our comprehensive platform.
            From cloud migration to AI implementation, we deliver results that scale with your ambition.
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex gap-4 flex-wrap"
          >
            <button
              className="btn-primary"
              onClick={() => scrollTo("#contact")}
              aria-label="Get started with Tekstation"
            >
              Start Free Trial
            </button>
            <button
              className="btn-outline"
              onClick={() => scrollTo("#services")}
              aria-label="View our services"
            >
              Book a Demo
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="flex gap-10 mt-12 flex-wrap"
            role="list"
            aria-label="Company metrics"
          >
            {stats.map(({ value, label }) => (
              <div key={label} role="listitem">
                <strong
                  className="font-display font-extrabold text-white block"
                  style={{ fontSize: "2.2rem", letterSpacing: "-0.03em", lineHeight: 1 }}
                >
                  {value}
                </strong>
                <span
                  className="text-xs uppercase tracking-widest block mt-1"
                  style={{ color: "var(--slate)" }}
                >
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right – floating cards (hidden on mobile) */}
        <div className="relative hidden lg:block" style={{ height: "420px" }} aria-hidden="true">
          {floatingCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                y: 0, 
                scale: 1,
                rotate: [0, 2, -2, 0]
              }}
              transition={{ 
                duration: 0.8, 
                delay: 0.6 + i * 0.2,
                type: "spring",
                stiffness: 60,
                damping: 20
              }}
              whileHover={{ 
                scale: 1.05,
                rotate: 0,
                transition: { duration: 0.4, type: "spring", stiffness: 400 }
              }}
              className={`absolute rounded-2xl p-6 backdrop-blur-[10px] ${card.cls}`}
              style={card.style}
            >
              {card.content}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
